import { NavRoute } from '@web-app/components/Navbar/types';
import { cn } from '@web-app/lib/utils';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';

interface NavLinkProps {
  route: NavRoute;
  href: string;
  badge?: ReactNode;
  /** Small status mark overlaid on the route icon (e.g. an unread dot). */
  iconIndicator?: ReactNode;
  variant?: 'default' | 'subItem';
}

export const NavLink = ({
  route,
  href,
  badge,
  iconIndicator,
  variant = 'default',
}: NavLinkProps): JSX.Element => {
  const router = useRouter();
  const isActive = router.route === route.path;
  const isSubItem = variant === 'subItem';

  return (
    <Link
      href={href}
      aria-current={isActive ? 'page' : undefined}
      data-active={isActive || undefined}
      className={cn(
        'flex w-full items-center rounded-[10px]',
        'text-base font-normal text-sidebar-foreground',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:no-underline',
        'outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring',
        'data-[active]:bg-sidebar-accent data-[active]:text-sidebar-accent-foreground',
        isSubItem ? 'h-7 px-2' : 'gap-2 px-4 py-2 [&>svg]:size-6',
      )}
    >
      {!isSubItem &&
        (iconIndicator ? (
          <span className="relative flex shrink-0 [&>svg]:size-6">
            {route.icon}
            <span className="absolute -top-0.5 -right-0.5 flex">
              {iconIndicator}
            </span>
          </span>
        ) : (
          route.icon
        ))}
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate">{route.name}</span>
        {badge}
      </span>
    </Link>
  );
};
